import { useEffect, useState } from "react";

// The announcement ends at the start of September 20 for every locale.
const EXPIRES_AT = Date.parse("2026-09-20T00:00:00Z");
const STORAGE_PREFIX = "pbtg.career-update.2026-09.clicked:";
const CLICKS_TO_DISMISS = 5;
const MAX_TIMEOUT_MS = 2_147_483_647;

function readClickCount(storageKey: string): number {
    try {
        const stored = window.localStorage.getItem(storageKey);
        // Preserve a click recorded by the original one-click badge.
        if (stored === "true") return 1;
        const count = Number(stored);
        return Number.isSafeInteger(count) && count >= 0
            ? Math.min(count, CLICKS_TO_DISMISS)
            : 0;
    } catch {
        return 0;
    }
}

/** Device-local announcement dismissal; it never changes Career progress. */
export function useCareerUpdateBadge(userId: string | null) {
    const storageKey = `${STORAGE_PREFIX}${userId ?? "browser"}`;
    const [clickCounts, setClickCounts] = useState<Record<string, number>>({});
    const clickCount = Math.max(
        clickCounts[storageKey] ?? 0,
        readClickCount(storageKey),
    );
    const [isBeforeExpiry, setIsBeforeExpiry] = useState(
        () => Date.now() < EXPIRES_AT,
    );

    useEffect(() => {
        let timer: number | undefined;
        const checkExpiry = () => {
            window.clearTimeout(timer);
            const remaining = EXPIRES_AT - Date.now();
            setIsBeforeExpiry(remaining > 0);
            if (remaining > 0) {
                timer = window.setTimeout(
                    checkExpiry,
                    Math.min(remaining, MAX_TIMEOUT_MS),
                );
            }
        };

        checkExpiry();
        // Recheck immediately when a suspended Telegram webview resumes.
        document.addEventListener("visibilitychange", checkExpiry);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener("visibilitychange", checkExpiry);
        };
    }, []);

    const markClicked = () => {
        const nextCount = Math.min(
            Math.max(clickCount, readClickCount(storageKey)) + 1,
            CLICKS_TO_DISMISS,
        );
        setClickCounts((previous) => ({
            ...previous,
            [storageKey]: nextCount,
        }));
        try {
            window.localStorage.setItem(storageKey, String(nextCount));
        } catch {
            // Navigation and the mounted UI still work if storage is blocked.
        }
    };

    return {
        isUpdated: isBeforeExpiry && clickCount < CLICKS_TO_DISMISS,
        markClicked,
    };
}
